import { useBoundingBox } from 'modules/Editor/components/BoundingBox/useBoundingBox';
import { RootState, store } from 'modules/Editor/store';
import { selectWidget } from 'modules/Editor/store/infographSelector';
import { useCallback } from 'react';
import { WidgetId } from 'types/idTypes';
import { WidgetType } from 'types/widget.types';
import { getGroupTargetDataFromWidgetId, setWidgetElementWrapperPosition } from 'utils/dom';
import { GroupWidgetData } from 'widgets/GroupWidget/GroupWidget.types';
import { getWidgetTypeFromId } from 'widgets/Widget.helpers';
import { groupIdCache } from 'widgets/sdk/GroupIdCache';
import { responsiveGroupBatchUpdate, responsiveShapeResize } from 'widgets/store';

import { resizeWidgets } from './useResponsiveGroup.helpers';

/**
 *
 * This hook handles the responsive behavior for text widgets that are part of a group widget.
 *
 * When the text widget changes height (e.g. typing and pressing enter for a new line / changing font size / etc.)
 * 1. Move widgets below/up relative to the height difference
 * 2. If a basic shape widget is wrapping around the text widget, increase the height of the basic shape widget
 *
 */
export const useResponsiveGroup = (widgetId: WidgetId) => {
  const { boundingBox } = useBoundingBox();

  const responsiveGroupResize = useCallback(
    ({
      height: textWidgetHeight,
      previousHeight: textWidgetPreviousHeight,
    }: {
      height: number | undefined;
      previousHeight: number | undefined;
    }) => {
      if (typeof textWidgetHeight !== 'number' || typeof textWidgetPreviousHeight !== 'number') return;

      const parentId = groupIdCache.getParentIdNested(widgetId);
      if (!parentId) return;

      const state = store.getState() as RootState;
      const groupWidget = selectWidget(parentId)(state);

      if (!groupWidget) return;

      const memberWidgetIds = (groupWidget as GroupWidgetData).memberWidgetIds;
      if (!memberWidgetIds || !memberWidgetIds.length) return;

      resizeWidgets({
        boundingBox,
        memberWidgetIds,
        parentId,
        widgetId,
        textWidgetHeight,
        textWidgetPreviousHeight,
        responsiveGroupBatchUpdate,
        responsiveShapeResize,
      });

      // Update group widget dimensions so flow mode listeners see changes in group widget dimensions
      const groupTargetData = getGroupTargetDataFromWidgetId(widgetId, groupWidget as GroupWidgetData);
      if (!!groupTargetData && getWidgetTypeFromId(parentId) === WidgetType.Group) {
        setWidgetElementWrapperPosition(parentId, {
          leftPx: groupTargetData.wrapperPos.leftPx,
          topPx: groupTargetData.wrapperPos.topPx,
          widthPx: groupTargetData.wrapperPos.widthPx,
          heightPx: groupTargetData.wrapperPos.heightPx,
        });
      }
    },
    [boundingBox, widgetId],
  );

  const hasUnsavedGroupMemberUpdates = useCallback(() => {
    const parentId = groupIdCache.getParentIdNested(widgetId);
    if (!parentId) return false;

    const batchUpdate = responsiveGroupBatchUpdate.getBatchUpdate(parentId);
    if (!batchUpdate) return false;

    return batchUpdate.length > 0;
  }, [widgetId]);

  return { responsiveGroupResize, hasUnsavedGroupMemberUpdates };
};
